<template>
  <section v-if="faqItems.length" id="faq" class="bg-white py-8 md:py-9 lg:py-20 px-4 sm:px-8 lg:px-12 xl:px-[120px]">
    <div class="w-full mx-auto">
      <!-- 2 Column Layout -->
      <div class="flex flex-col lg:flex-row lg:justify-between gap-8 lg:gap-12">
        <!-- Left Column: Title & Support -->
        <div class="flex flex-col items-center lg:items-start text-center lg:text-left lg:max-w-[300px] flex-shrink-0">
          <!-- Badge -->
          <span class="text-xs md:text-sm font-medium leading-5 text-[#36393E] mb-0.5 md:mb-2 lg:mb-2">
            {{ badge || 'GIẢI ĐÁP THẮC MẮC' }}
          </span>
          
          <!-- Title -->
          <h2 class="font-display text-lg md:text-xl lg:text-2xl font-bold leading-6 md:leading-7 lg:leading-8 mb-0.5 md:mb-2 lg:mb-2">
            <span class="bg-gradient-to-r from-[#892DDE] to-[#4A1878] bg-clip-text text-transparent">
              {{ title || 'CÂU HỎI THƯỜNG GẶP' }}
            </span>
          </h2>
          
          <!-- Support Text -->
          <p class="text-xs md:text-sm font-normal leading-4 md:leading-5 text-[#5C636C] mb-4 md:mb-6 lg:mb-10">
            {{ supportText }}
          </p>
          
          <!-- Support Link -->
          <button
            type="button"
            class="inline-flex items-center gap-1.5 text-xs md:text-sm font-semibold leading-5 text-[#8326D9] transition-all duration-200 hover:underline"
            @click="$emit('request-support')"
          >
            <span>YÊU CẦU HỖ TRỢ NGAY</span>
            <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg" class="rotate-[-45deg]">
              <path d="M6 1L11 7L6 13M11 7H1" stroke="#8326D9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        
        <!-- Right Column: FAQ List -->
        <div class="flex-1">
          <div
            v-for="(faq, index) in faqItems"
            :key="faq.question"
            class="border-b border-[#F0F1F2]"
          >
            <button
              type="button"
              class="w-full flex items-center justify-between py-4 md:py-5 lg:py-6 text-left"
              @click="toggleFaq(index)"
            >
              <h3 
                :class="[
                  'text-sm md:text-base lg:text-lg font-semibold leading-5 md:leading-6 lg:leading-7 pr-4 transition-colors duration-200',
                  openIndexes.has(index) ? 'text-[#8326D9]' : 'text-[#36393E]'
                ]"
              >
                {{ faq.question }}
              </h3>
              <svg 
                :class="[
                  'w-5 h-5 md:w-6 md:h-6 flex-shrink-0 transition-all duration-300 ease-out',
                  openIndexes.has(index) ? 'rotate-180' : ''
                ]"
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6 9L12 15L18 9" :stroke="openIndexes.has(index) ? '#8326D9' : '#36393E'" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            
            <!-- Answer (collapsible) -->
            <div 
              :ref="el => setAnswerRef(el, index)"
              class="faq-answer overflow-hidden transition-all duration-300 ease-out"
              :style="{ 
                maxHeight: openIndexes.has(index) ? `${answerHeights[index] || 500}px` : '0px',
                opacity: openIndexes.has(index) ? 1 : 0
              }"
            >
              <div class="pb-4 md:pb-5 lg:pb-6">
                <p class="text-xs md:text-sm lg:text-base font-normal leading-4 md:leading-5 lg:leading-6 text-[#36393E]">
                  {{ faq.answer }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, reactive, onMounted, nextTick } from 'vue';

defineEmits(['request-support']);

const props = defineProps({
  content: {
    type: Object,
    default: () => ({}),
  },
});

// Use Set to allow multiple FAQs open
const openIndexes = ref(new Set([0])); // First FAQ open by default
const answerRefs = reactive({});
const answerHeights = reactive({});

const sectionContent = computed(() => {
  if (Array.isArray(props.content)) {
    return { items: props.content };
  }
  return props.content || {};
});

const faqItems = computed(() =>
  (Array.isArray(sectionContent.value.items) ? sectionContent.value.items : []).filter(
    item => item?.question && item?.answer
  )
);

const badge = computed(() => sectionContent.value.badge || 'GIẢI ĐÁP THẮC MẮC');
const title = computed(() => sectionContent.value.title || 'CÂU HỎI THƯỜNG GẶP');
const supportText = computed(() => 
  sectionContent.value.support_text || 
  'Bạn chưa tìm thấy câu trả lời mình cần? Đội ngũ hỗ trợ của HiCHAT luôn sẵn sàng giải đáp mọi thắc mắc của bạn qua Live Chat hoặc Hotline.'
);

const setAnswerRef = (el, index) => {
  if (el) {
    answerRefs[index] = el;
    // Measure the natural height
    nextTick(() => {
      const inner = el.querySelector('div');
      if (inner) {
        answerHeights[index] = inner.scrollHeight;
      }
    });
  }
};

const toggleFaq = (index) => {
  const newSet = new Set(openIndexes.value);
  if (newSet.has(index)) {
    newSet.delete(index);
  } else {
    newSet.add(index);
    // Recalculate height when opening
    nextTick(() => {
      const el = answerRefs[index];
      if (el) {
        const inner = el.querySelector('div');
        if (inner) {
          answerHeights[index] = inner.scrollHeight;
        }
      }
    });
  }
  openIndexes.value = newSet;
};

onMounted(() => {
  // Calculate initial heights
  nextTick(() => {
    Object.keys(answerRefs).forEach(index => {
      const el = answerRefs[index];
      if (el) {
        const inner = el.querySelector('div');
        if (inner) {
          answerHeights[index] = inner.scrollHeight;
        }
      }
    });
  });
});
</script>

<style scoped>
.faq-answer {
  will-change: max-height, opacity;
}
</style>
